import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { cloneDeep } from 'lodash-es';
import {
  postV7DashboardsUserFilterAddByDashboardId,
  patchV7DashboardsUserFilterDeleteByDashboardId,
  getV7DashboardsUserFilterGetByDashboardId,
} from '@/api-client/dashboard/queries/integrated-dashboard-filter-v7-controller/integrated-dashboard-filter-v7-controller';
import { IntegrateDashboardUserFilterRequest, UserFilterItem } from '@/api-client/dashboard/model';
import { GlobalVarFilterDataForView } from '@/dashboard/features/globalVariables/components/globalVarFilter/types/globalVarFilter.type';
import { useInternational } from '@/common/locale';
import { FRAME_NAMES } from '@/common/define/apiTrace.define';
import type {
  GlobalVariable,
  SelectedGlobalVariable,
  SelectedTreeVariable,
  FilterVariablePreset,
} from '@/dashboard/features/globalVariables/domain/globalVariable.types';
import {
  createGlobalVariableFilter,
  createExternalVariableFilter,
  createTreeVariableFilter,
} from '@/dashboard/features/globalVariables/domain/variablePresets.utils';
import type { ExternalVariablesOptions } from '@/dashboard/features/globalVariables/components/externalVariableWindow/types/externalVariableWindow.type';
import type { TreeVarFilterDataForView } from '@/dashboard/features/globalVariables/components/globalVarFilter/types/globalVarFilter.type';
import { useUserEnvStore } from '@/common/stores/user-env';

export const useVariablePresetsStore = defineStore('variablePresets', () => {
  const { t } = useInternational();
  const userEnvStore = useUserEnvStore();

  const DEFAULT_PRESET_ID = -1;
  const CURRENT_PRESET_ENV_KEY = 'DASHBOARD_CURRENT_PRESET_ID';

  const defaultPreset = ref<FilterVariablePreset>({
    userFilterId: DEFAULT_PRESET_ID,
    name: t('WORD.DEFAULT_PRESET'),
    filters: [],
    treeFilters: [],
  });

  const presetList = ref<FilterVariablePreset[]>([defaultPreset.value]);
  const currentPreset = ref<FilterVariablePreset>(defaultPreset.value);

  const currentPresetUserEnv = computed<Record<number, number>>(() => {
    return userEnvStore.getEnvValue<Record<number, number>>(CURRENT_PRESET_ENV_KEY) ?? {};
  });

  const getPresetFilters = (
    data: UserFilterItem,
    globalVariables: GlobalVariable[],
    externalVariables: ExternalVariablesOptions[],
  ): SelectedGlobalVariable[] => {
    const findFilter = (id: number) =>
      data?.filters?.find(({ globalFilterId }) => globalFilterId === id);

    const globalFilters = globalVariables.map((variable) =>
      createGlobalVariableFilter(variable, findFilter(variable.globalFilterId)),
    );

    const externalFilters = externalVariables.map((variable) =>
      createExternalVariableFilter(variable, findFilter(variable.extraGlobalFilterId!)),
    );

    return [...globalFilters, ...externalFilters];
  };

  const updateDefaultPresetFilters = (
    globalVariables: GlobalVariable[],
    externalVariables: ExternalVariablesOptions[],
    treeVariables: TreeVarFilterDataForView[],
  ) => {
    if (!globalVariables.length && !externalVariables.length && !treeVariables.length) {
      defaultPreset.value.filters = [];
      defaultPreset.value.treeFilters = [];
      return;
    }

    defaultPreset.value.filters = [
      ...globalVariables.map((v) => createGlobalVariableFilter(v)),
      ...externalVariables.map((v) => createExternalVariableFilter(v)),
    ];
    defaultPreset.value.treeFilters = treeVariables.map((v) => createTreeVariableFilter(v));
  };

  const getPresetTreeFilters = (
    data: UserFilterItem,
    treeVariables: TreeVarFilterDataForView[],
  ): SelectedTreeVariable[] => {
    return (
      data?.filters
        ?.filter((f) => f.type === 'EXEMONE_TREE')
        .map((f) => {
          const treeVar = treeVariables.find((v) => v.id === f.globalFilterId);
          return {
            treeVariableId: f.globalFilterId!,
            use: f.enable ?? true,
            operationType: treeVar?.operationType ?? 'OR',
            targetFilterType: treeVar?.targetFilterType ?? 'MANUAL',
            selectedList:
              f.targets?.map(({ tagKey, tagType, tagValueId }) => ({
                tagKey: tagKey ?? '',
                tagType: tagType ?? '',
                tagValueId: tagValueId ?? '',
              })) ?? [],
          };
        }) ?? []
    );
  };

  const fetchPresetList = async (
    dashboardId: number,
    globalVariables: GlobalVariable[],
    externalVariables: ExternalVariablesOptions[],
    treeVariables: TreeVarFilterDataForView[],
  ) => {
    try {
      const response = await getV7DashboardsUserFilterGetByDashboardId(
        dashboardId,
        {},
        { params: { frameName: FRAME_NAMES.DASHBOARD.PRESET_LIST } },
      );

      const presets: FilterVariablePreset[] =
        response?.data?.data?.map((data) => ({
          name: data?.name ?? '',
          userFilterId: data?.userFilterId ?? -1,
          filters: getPresetFilters(data, globalVariables, externalVariables),
          treeFilters: getPresetTreeFilters(data, treeVariables),
        })) ?? [];

      presetList.value = [defaultPreset.value, ...presets];
    } catch {
      //
    }
  };

  const saveCurrentPresetUserEnv = async (dashboardId: number, presetId: number) => {
    if (dashboardId !== -1) {
      const updated = { ...currentPresetUserEnv.value, [dashboardId]: presetId };
      await userEnvStore.setEnvValue([{ key: CURRENT_PRESET_ENV_KEY, value: updated }]);
    }
  };

  const setCurrentPresetById = (presetId: number) => {
    const target =
      presetList.value.find(({ userFilterId }) => userFilterId === presetId) ?? defaultPreset.value;
    currentPreset.value = target;
    return cloneDeep({ filters: target.filters, treeFilters: target.treeFilters });
  };

  const setDefaultPreset = () => {
    return setCurrentPresetById(DEFAULT_PRESET_ID);
  };

  const initCurrentPreset = (dashboardId: number) => {
    const presetId = currentPresetUserEnv.value[dashboardId ?? -1] ?? DEFAULT_PRESET_ID;
    return setCurrentPresetById(presetId);
  };

  const savePreset = async (
    dashboardId: number,
    data: {
      name: string;
      filters: GlobalVarFilterDataForView[];
      treeFilters?: SelectedTreeVariable[];
    },
  ): Promise<number | undefined> => {
    if (dashboardId !== -1) {
      const standardFilters =
        data.filters?.map(({ globalFilterId, selectedList, use, external }) => ({
          globalFilterId,
          targets: selectedList.map((selected) => {
            const [tagKey, tagType, tagValueId] = selected.split('--');
            return {
              tagKey,
              tagType: tagType ?? 'tag',
              tagValueId: tagValueId ?? tagKey,
            };
          }),
          isEnable: use,
          type: external?.extraType || 'EXEMONE',
        })) ?? [];

      const treeFilterRequests =
        data.treeFilters?.map(({ treeVariableId, use, selectedList }) => ({
          globalFilterId: treeVariableId,
          targets: selectedList.map(({ tagKey, tagType, tagValueId }) => ({
            tagKey,
            tagType,
            tagValueId,
          })),
          isEnable: use,
          type: 'EXEMONE_TREE',
        })) ?? [];

      const request: IntegrateDashboardUserFilterRequest = {
        filters: [...standardFilters, ...treeFilterRequests],
      };

      const response = await postV7DashboardsUserFilterAddByDashboardId(dashboardId, request, {
        name: data.name,
      });

      return (
        response?.data?.userFilterId ??
        presetList.value?.[presetList.value.length - 1]?.userFilterId
      );
    }
    return undefined;
  };

  const deletePreset = async (dashboardId: number, targetUserFilterIds: number[]) => {
    if (dashboardId !== -1) {
      await Promise.all(
        targetUserFilterIds.map((id) =>
          patchV7DashboardsUserFilterDeleteByDashboardId(dashboardId, {
            userFilterId: id,
          }),
        ),
      );

      if (targetUserFilterIds.includes(currentPreset.value.userFilterId)) {
        setCurrentPresetById(DEFAULT_PRESET_ID);
        await saveCurrentPresetUserEnv(dashboardId, DEFAULT_PRESET_ID);
      }
    }
  };

  const fetchCurrentPresetEnv = async () => {
    await userEnvStore.fetchEnvDataByKeys([CURRENT_PRESET_ENV_KEY]);
  };

  const resetState = () => {
    defaultPreset.value = {
      userFilterId: DEFAULT_PRESET_ID,
      name: t('WORD.DEFAULT_PRESET'),
      filters: [],
      treeFilters: [],
    };
    presetList.value = [defaultPreset.value];
    currentPreset.value = defaultPreset.value;
  };

  return {
    presetList,
    currentPreset,
    defaultPreset,

    updateDefaultPresetFilters,
    fetchPresetList,
    setCurrentPresetById,
    setDefaultPreset,
    fetchCurrentPresetEnv,
    initCurrentPreset,
    saveCurrentPresetUserEnv,
    savePreset,
    deletePreset,
    resetState,
  };
});
